import { create } from 'zustand';
import type { DiveLogInput } from '@/data/dive-logs';

/**
 * Simple offline queue persisted to localStorage. When the user logs a dive
 * while offline, we stash the payload here and flush it on next online event.
 *
 * A future iteration can move this to IndexedDB for larger payloads (photos),
 * but for current dive-log JSON shapes localStorage is plenty.
 */

const STORAGE_KEY = 'divelogapp.offline-queue.v1';

interface QueuedDiveLog {
  id: string;
  createdAt: string;
  payload: DiveLogInput;
}

interface OfflineQueueState {
  items: QueuedDiveLog[];
  isOnline: boolean;
  enqueue: (payload: DiveLogInput) => QueuedDiveLog;
  remove: (id: string) => void;
  setOnline: (online: boolean) => void;
}

function loadInitial(): QueuedDiveLog[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as QueuedDiveLog[]) : [];
  } catch {
    return [];
  }
}

function persist(items: QueuedDiveLog[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* localStorage full or disabled – swallow */
  }
}

export const useOfflineQueue = create<OfflineQueueState>((set, get) => ({
  items: typeof window === 'undefined' ? [] : loadInitial(),
  isOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
  enqueue: (payload) => {
    const item: QueuedDiveLog = {
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      payload,
    };
    const items = [...get().items, item];
    persist(items);
    set({ items });
    return item;
  },
  remove: (id) => {
    const items = get().items.filter((i) => i.id !== id);
    persist(items);
    set({ items });
  },
  setOnline: (online) => set({ isOnline: online }),
}));

// Wire up the browser online/offline events once on module load.
if (typeof window !== 'undefined') {
  window.addEventListener('online',  () => useOfflineQueue.getState().setOnline(true));
  window.addEventListener('offline', () => useOfflineQueue.getState().setOnline(false));
}
